import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * ErrorBoundary — keeps one broken panel from taking the whole app down.
 *
 * WHY THIS MATTERS MORE HERE THAN IN A TYPICAL APP
 * ------------------------------------------------
 * The solved-but-unpublished roster lives ONLY in the browser (see App.js /
 * useRoster — `/shifts-range` returns published assignments, and Publish is the
 * only thing that makes a draft durable). With no boundary, a single render-time
 * TypeError unmounts the entire tree to a blank white page, and the planner's
 * draft goes with it: a monthly solve is several minutes of work and there is no
 * way back to it.
 *
 * So this is deliberately mounted AROUND THE TAB CONTENT, not around the whole
 * app. `schedule` is held in App's state, above this boundary, so a tab that
 * throws is contained: the sidebar still works, the draft survives in memory,
 * and switching away and back re-mounts the panel cleanly. Wrapping <App/> in
 * index.js instead would show a nicer message and still lose everything.
 *
 * It also SAYS what broke. The previous failure mode was a white screen with the
 * real error visible only in the browser console — which a clinical UAT tester
 * has no reason to open, so the bug came back as "the app died" with nothing to
 * act on.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Still log it: the console trace is what a developer needs, and the panel
    // below is what the operator needs. Both, not either.
    console.error('[ErrorBoundary] render error in', this.props.label || 'a panel', error, info);
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const label = this.props.label || 'This section';
    return (
      <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/60 p-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white rounded-xl border border-rose-200 text-rose-500 shadow-sm shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-black text-rose-900 tracking-tight">
              {label} hit an error and stopped drawing
            </h3>
            <p className="text-[11px] text-rose-700 font-bold mt-1 leading-relaxed">
              The rest of the app is still working, and any solved roster you have
              not published yet is still held in this tab — switch to another tab
              and back, or press Try again. Do not reload the page if you have an
              unpublished roster: reloading discards it.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={this.reset}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border-2 border-rose-200 text-[11px] font-bold text-rose-700 hover:border-rose-300 transition-all"
              >
                <RotateCcw className="w-3 h-3" /> Try again
              </button>
            </div>
            <details className="mt-3">
              <summary className="text-[10px] font-black uppercase tracking-widest text-rose-400 cursor-pointer select-none">
                Details for the developer
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-white/70 border border-rose-100 p-2 text-[10px] leading-snug text-rose-900 whitespace-pre-wrap">
{String(error && (error.stack || error.message || error))}
{info && info.componentStack ? `\n--- component stack ---${info.componentStack}` : ''}
              </pre>
            </details>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
